(() => {
  'use strict';
  const mountSelector = '[data-gardening-stitch-price], [data-gardening-stitch-cart-price-selector]';
  const barSelector = '[data-gardening-stitch-mobile-buy]';
  const relevantSelector = `${mountSelector}, ${barSelector}, #shopify-pc__banner, [aria-modal="true"]`;
  const instances = new Map();
  const bars = new Map();
  let root;
  let rootObserver;
  let bodyObserver;
  let queued = false;

  function makeLink(mount, className) {
    const link = document.createElement('a');
    link.href = 'https://www.stitch.me/pay-later';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.className = className;
    link.setAttribute('aria-label', mount.dataset.gardeningStitchLinkLabel);
    return link;
  }

  function enhance(mount) {
    const state = instances.get(mount);
    if (!state || !mount.isConnected) return;
    state.links.disconnect();
    const logo = mount.querySelector('.stitch-bnpl-widget__logo');
    if (logo && !logo.closest('.gardening-stitch-logo-link')) {
      const link = makeLink(mount, 'gardening-stitch-logo-link');
      logo.replaceWith(link);
      link.append(logo);
    }
    const body = mount.querySelector('.stitch-bnpl-widget__body');
    if (body && !body.querySelector('.gardening-stitch-text-link')) {
      const wording = body.textContent;
      const brand = 'Stitch Pay Later';
      const position = wording.lastIndexOf(brand);
      if (position >= 0) {
        const link = makeLink(mount, 'gardening-stitch-text-link');
        link.textContent = brand;
        body.replaceChildren(document.createTextNode(wording.slice(0, position)), link,
          document.createTextNode(wording.slice(position + brand.length)));
      }
    }
    mount.classList.toggle('gardening-stitch-pay-later--below-minimum',
      !mount.querySelector('.stitch-bnpl-widget__card'));
    state.links.observe(mount, { childList: true, subtree: true, characterData: true });
  }

  function visible(element) {
    return element && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
  }

  function refreshBar(bar, state) {
    const obstructed = Array.from(document.querySelectorAll('#shopify-pc__banner, [aria-modal="true"]')).some(visible);
    bar.hidden = state.originalVisible || state.original.disabled || obstructed;
  }

  function observeBanner(state) {
    const banner = document.getElementById('shopify-pc__banner');
    if (banner === state.banner) return;
    if (state.banner) state.visibility.unobserve(state.banner);
    state.banner = banner;
    if (banner) state.visibility.observe(banner);
  }

  function syncBars() {
    for (const [bar, state] of bars) {
      const original = document.getElementById(bar.dataset.formId)?.querySelector('button[name="add"]');
      if (!bar.isConnected || original !== state.original) {
        state.visibility.disconnect();
        state.availability.disconnect();
        bars.delete(bar);
      }
    }
    if (!root) return;
    root.querySelectorAll(barSelector).forEach(bar => {
      const existing = bars.get(bar);
      if (existing) {
        observeBanner(existing);
        refreshBar(bar, existing);
        return;
      }
      const original = document.getElementById(bar.dataset.formId)?.querySelector('button[name="add"]');
      if (!original || !('IntersectionObserver' in window)) return;
      const state = { original, originalVisible: true };
      state.visibility = new IntersectionObserver(entries => {
        const entry = entries.find(item => item.target === original);
        if (entry) state.originalVisible = entry.isIntersecting;
        refreshBar(bar, state);
      });
      state.availability = new MutationObserver(() => refreshBar(bar, state));
      state.visibility.observe(original);
      observeBanner(state);
      state.availability.observe(original, { attributes: true, attributeFilter: ['disabled'] });
      bars.set(bar, state);
    });
  }

  function sync() {
    queued = false;
    const nextRoot = document.getElementById('MainContent') || document.querySelector('main');
    if (nextRoot !== root) {
      rootObserver?.disconnect();
      root = nextRoot;
      if (root) {
        rootObserver = new MutationObserver(records => {
          if (records.some(record => record.type === 'attributes' ||
            [...record.addedNodes, ...record.removedNodes].some(relevantNode))) schedule();
        });
        rootObserver.observe(root, { childList: true, subtree: true, attributes: true,
          attributeFilter: ['data-gardening-stitch-price', 'data-gardening-stitch-cart-price-selector'] });
      }
    }
    for (const [mount, state] of instances) {
      if (!mount.isConnected || !root?.contains(mount)) {
        state.links.disconnect();
        state.widget.destroy();
        instances.delete(mount);
      }
    }
    syncBars();
    if (!root || !window.StitchBNPL) return;
    root.querySelectorAll(mountSelector).forEach(mount => {
      const priceSelector = mount.dataset.gardeningStitchCartPriceSelector;
      const price = priceSelector ? null : Number(mount.dataset.gardeningStitchPrice);
      if (!priceSelector && !Number.isFinite(price)) return;
      const existing = instances.get(mount);
      if (existing) {
        if (existing.price !== price || existing.priceSelector !== priceSelector) {
          existing.price = price;
          existing.priceSelector = priceSelector;
          existing.widget.update({ price, priceSelector: priceSelector || null });
          enhance(mount);
        }
        return;
      }
      const links = new MutationObserver(() => enhance(mount));
      const widget = window.StitchBNPL.createWidget({ mountSelector: `#${CSS.escape(mount.id)}`,
        price, priceSelector: priceSelector || null, logoBg: 'purple', width: '100%',
        locale: 'en-ZA', currency: 'ZAR' });
      instances.set(mount, { widget, links, price, priceSelector });
      enhance(mount);
    });
  }

  function relevantNode(node) {
    return node.nodeType === Node.ELEMENT_NODE &&
      (node.matches(relevantSelector) || node.querySelector(relevantSelector) || node === root || node.contains(root));
  }

  function schedule() {
    if (queued) return;
    queued = true;
    queueMicrotask(sync);
  }

  function start() {
    if (!bodyObserver) {
      bodyObserver = new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(relevantNode))) schedule();
      });
      bodyObserver.observe(document.body, { childList: true });
    }
    sync();
  }

  ['on:variant:change', 'variant:change', 'shopify:section:load', 'shopify:section:unload'].forEach(event =>
    document.addEventListener(event, schedule));
  document.addEventListener('click', schedule, { passive: true });
  window.addEventListener('pageshow', start);
  window.addEventListener('pagehide', () => {
    rootObserver?.disconnect();
    bodyObserver?.disconnect();
    bodyObserver = null;
    rootObserver = null;
    root = null;
    for (const [mount, state] of instances) {
      state.links.disconnect();
      state.widget.destroy();
    }
    instances.clear();
    for (const state of bars.values()) {
      state.visibility.disconnect();
      state.availability.disconnect();
    }
    bars.clear();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
